import { FC, useContext, useMemo } from "react";
import { EnterpriseComplianceFiltersContext } from "./contexts/EcdFiltersContext";
import { AccordionContainer, Accordion } from "@app/shared";
import { DropdownItem } from "@app/shared/checkbox-dropdown/types";
import { calculateServiceAreaSelections } from "@app/shared/service-area-dropdown/helpers";
import { FlyoutLink } from "@app/shared/Flyouts";
import EcdSaveFiltersFlyout from "./flyouts/EcdSaveFiltersFlyout";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";

const EnterpriseComplianceFiltersOverview: FC = () => {
    const filtersContext = useContext(EnterpriseComplianceFiltersContext);

    const totalFiltersSelected = useMemo(() => {
        if (!filtersContext.apiReadyFilters) {
            return 0;
        }
        return (
            filtersContext.apiReadyFilters.canonicalRegionSetIds.length +
            filtersContext.apiReadyFilters.networkTaxonomyLOBs.length +
            filtersContext.filterProjects.length +
            filtersContext.apiReadyFilters.stateAbbreviations.length +
            filtersContext.apiReadyFilters.countyStatuses.length +
            filtersContext.apiReadyFilters.specialtyGroupIds.length +
            filtersContext.filterTags.length
        );
        // This memo _does_ depend on filtersContext.filterProjects and filtersContext.filterTags,
        // but we only want it to _update_ when the api filters are updated.
        // Otherwise the count updates on every checkbox click for tags and projects, but the 
        // list of filters displayed does not, because they haven't been applied yet.
        // And to clarify why, filterTags aren't included in apiReadyFilters, and apiReadyFilters.projects
        // is the intersected list of selected projects and projects available from the tags api filtering, so
        // it may not reflect the user's selections.
    }, [filtersContext.apiReadyFilters]);

    const countyToStateMap = useMemo(() => {
        const map = new Map<string, string>();
        filtersContext.serviceAreaOptions.forEach((state) => state.children.forEach((county) => map.set(county.key, state.name)));
        return map;
    }, [filtersContext.serviceAreaOptions]);

    const displayFilters = useMemo(() => {
        const tags = filtersContext.filterTags.map((tagString) => {
            const [, group, tag] = tagString.split("§");
            return <span key={tagString} className="dashboard-filters-overview__filter">{group}: {tag}</span>;
        });

        const lineOfBusiness = filtersContext.filterLineOfBusiness.map((lob) => <span key={lob.key} className="dashboard-filters-overview__filter">{lob.name}</span>);

        const { counties, states } = calculateServiceAreaSelections(filtersContext.serviceAreaOptions, filtersContext.filterServiceArea);
        const stateFilters = filtersContext.serviceAreaOptions
            .filter((state) => states.includes(state.key))
        .map((state) => <span key={state.key} className="dashboard-filters-overview__filter">{state.name}</span>);

        const countyFilters = filtersContext.serviceAreaOptions
            .flatMap((sa) => sa.children)
            .filter((county) => counties.includes(county.key))
            .map((county) => (
                <span key={county.key} className="dashboard-filters-overview__filter">
                    {`${(county as DropdownItem<string>).name}, ${countyToStateMap.get(county.key)}`}
                </span>
            ));

        const specialties = filtersContext.filterSpecialty.map((sp) => <span key={sp.key} className="dashboard-filters-overview__filter">{sp.name}</span>);

        const countyStatuses = filtersContext.filterCountyStatus.map((cs) => <span key={cs.key} className="dashboard-filters-overview__filter">{cs.name}</span>);

        const includedProjectNames: string[] = [];
        const excludedProjectNames: string[] = [];
        const hasProjectsSelected = filtersContext.filterProjects.length > 0;
        const projects = hasProjectsSelected ? filtersContext.filterProjects : filtersContext.projectsOptions;
        projects.forEach((project) => {
            if (filtersContext.excludedProjectIds.includes(parseInt(project.key))) {
                excludedProjectNames.push(project.name);
            } else {
                includedProjectNames.push(project.name);
            }
        })
        const includedProjects = hasProjectsSelected ? includedProjectNames.map((project) => <span key={project} className="dashboard-filters-overview__filter">{project}</span>) : [];
        const excludedProjects = excludedProjectNames.map((project) => <span key={project} className="dashboard-filters-overview__filter">{project}</span>);

        return {
            counties: countyFilters,
            countyStatuses,
            excludedProjects,
            includedProjects,
            lineOfBusiness,
            specialties,
            states: stateFilters,
            tags,
        };

        // This memo _does_ depend on filtersContext.filterServiceArea and .serviceAreaOptions,
        // but we don't want it to update until apiReadyFilters is updated.
        // apiReadyFilters is set to have an empty array if all of a service area (or anything else) 
        // is selected, so pulling directly from apiReadyFilters here isn't strictly accurate.
    }, [filtersContext.apiReadyFilters, filtersContext.excludedProjectIds]);

    const titleString = useMemo(() => {
        if (filtersContext.isDashboardLoading || !filtersContext.apiReadyFilters) {
            return <>Filters: <QesIcon iconName={QesIcons.FontAwesome.Spinner} classNames="qes-icon-spin" width="14" height="14" description="loading filters" /></>;
        }
        const excludedSegment = displayFilters.excludedProjects.length > 0 ? "(Projects may be limited due to applied tags)" : "";
        return `Filters: ${totalFiltersSelected.toLocaleString()} Applied ${excludedSegment}`;
    }, [totalFiltersSelected, displayFilters.excludedProjects, filtersContext.isDashboardLoading]);

    return (
        <AccordionContainer>
            <Accordion
                testId="ecd-filter-overview-accordion"
                title={titleString}
            >
                <div className="d-flex dashboard-filters-overview__list">
                    <span className="dashboard-filters-overview__label">Tag:</span>
                    <span>{displayFilters.tags}</span>
                </div>
                <div className="d-flex dashboard-filters-overview__list">
                    <span className="dashboard-filters-overview__label">Line of Business:</span>
                    <span>{displayFilters.lineOfBusiness}</span>
                </div>
                <div className="d-flex dashboard-filters-overview__list">
                    <span className="dashboard-filters-overview__label">State - All Licensed Counties:</span>
                    <span>{displayFilters.states}</span>
                </div>
                <div className="d-flex dashboard-filters-overview__list">
                    <span className="dashboard-filters-overview__label">County:</span>
                    <span>{displayFilters.counties}</span>
                </div>
                <div className="d-flex dashboard-filters-overview__list">
                    <span className="dashboard-filters-overview__label">Specialty:</span>
                    <span>{displayFilters.specialties}</span>
                </div>
                <div className="d-flex dashboard-filters-overview__list">
                    <span className="dashboard-filters-overview__label">County Status:</span>
                    <span>{displayFilters.countyStatuses}</span>
                </div>
                <div className="d-flex dashboard-filters-overview__list">
                    <span className="dashboard-filters-overview__label">Project - Applied:</span>
                    <span>{displayFilters.includedProjects}</span>
                </div>
                <div className="d-flex dashboard-filters-overview__list">
                    <span className="dashboard-filters-overview__label">Project - Excluded by Selected Tags:</span>
                    <span>{displayFilters.excludedProjects}</span>
                </div>
                <div className="d-flex justify-content-end">
                    <FlyoutLink
                        testId="ecd-filters-overview__save-filters-flyout"
                        classNames="qes-button qes-button-primary"
                        content={{
                            body: <EcdSaveFiltersFlyout />,
                            title: "Save Filters",
                        }}
                    >
                        Save Filters
                    </FlyoutLink>
                </div>
            </Accordion>
        </AccordionContainer>
    );
}

export default EnterpriseComplianceFiltersOverview;
